/**
 * URL state management for Space Yield Report.
 * All filter state lives in URL search params — no Zustand store needed.
 * Browser back/forward, bookmarking, and sharing all work.
 */

import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { format, subMonths, startOfMonth } from 'date-fns'
import type { ReportParams, GroupByOption, UnitStatus, RevenueSegment, DbUnitType, RevenueStreamId } from './types'
import {
  DEFAULT_TYPES,
  ALL_CATEGORY_TYPES,
  DEFAULT_VISIBLE_COLUMNS,
  CORE_COLUMNS,
  DEFAULT_COLUMNS,
  EXTENDED_COLUMNS,
  FUEL_COLUMNS,
  REVENUE_STREAMS,
  DEFAULT_STREAMS,
} from './types'

const VALID_SEGMENTS = ['all', 'lease', 'transient', 'idle'] as const
const VALID_GROUP_BY: GroupByOption[] = ['building', 'type', 'status', 'segment', 'none']
const ALL_TYPES_SET = new Set<string>(ALL_CATEGORY_TYPES)
const DEFAULT_TYPES_SORTED = [...DEFAULT_TYPES].sort().join(',')

// Column IDs for validation
const ALL_DEFAULT_COL_IDS = new Set(DEFAULT_COLUMNS.map((c) => c.id))
const ALL_EXTENDED_COL_IDS = new Set([
  ...EXTENDED_COLUMNS.map((c) => c.id),
  ...FUEL_COLUMNS.map((c) => c.id),
])
const CORE_COL_IDS = new Set(CORE_COLUMNS.map((c) => c.id))

// Revenue stream validation
const VALID_STREAM_IDS = new Set<string>(REVENUE_STREAMS.map((s) => s.id))
const DEFAULT_STREAMS_SORTED = [...DEFAULT_STREAMS].sort().join(',')

/** Default period: last completed calendar month */
function defaultPeriod(): string {
  return format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM')
}

/** Parse types param: comma-separated DbUnitType values */
function parseTypes(raw: string | null): DbUnitType[] {
  if (!raw) return DEFAULT_TYPES
  const types = raw.split(',').filter((t) => ALL_TYPES_SET.has(t)) as DbUnitType[]
  return types.length > 0 ? types : DEFAULT_TYPES
}

/** Parse streams param: comma-separated RevenueStreamId values */
function parseStreams(raw: string | null): RevenueStreamId[] {
  if (!raw) return [...DEFAULT_STREAMS]
  const streams = raw.split(',').filter((s) => VALID_STREAM_IDS.has(s)) as RevenueStreamId[]
  return streams.length > 0 ? streams : [...DEFAULT_STREAMS]
}

/**
 * Parse columns param with diff-encoding:
 * - No param = default columns (core + default)
 * - `-sqft` = hide default column sqft
 * - `annual_value` = show extended column annual_value
 */
function parseColumns(raw: string | null): string[] {
  if (!raw) return [...DEFAULT_VISIBLE_COLUMNS]

  const cols = new Set([...DEFAULT_VISIBLE_COLUMNS])
  const tokens = raw.split(',').filter(Boolean)

  for (const token of tokens) {
    if (token.startsWith('-')) {
      const id = token.slice(1)
      // Only default columns can be hidden (not core)
      if (ALL_DEFAULT_COL_IDS.has(id)) {
        cols.delete(id)
      }
    } else {
      // Only extended columns can be added
      if (ALL_EXTENDED_COL_IDS.has(token)) {
        cols.add(token)
      }
    }
  }

  return Array.from(cols)
}

/**
 * Encode columns to diff-encoding for URL param.
 * Returns null when columns match defaults (clean URL).
 */
function encodeColumns(columns: string[]): string | null {
  const colSet = new Set(columns)
  const tokens: string[] = []

  // Check for hidden default columns
  for (const col of DEFAULT_COLUMNS) {
    if (!colSet.has(col.id)) {
      tokens.push(`-${col.id}`)
    }
  }

  // Check for shown extended columns
  for (const col of EXTENDED_COLUMNS) {
    if (colSet.has(col.id)) {
      tokens.push(col.id)
    }
  }

  return tokens.length > 0 ? tokens.join(',') : null
}

export function useReportParams() {
  const [searchParams, setSearchParams] = useSearchParams()

  const params: ReportParams = useMemo(() => {
    const period = searchParams.get('period') || defaultPeriod()
    const types = parseTypes(searchParams.get('types'))

    const segmentRaw = searchParams.get('segment') || 'all'
    const segment = VALID_SEGMENTS.includes(segmentRaw as typeof VALID_SEGMENTS[number])
      ? (segmentRaw as 'all' | RevenueSegment)
      : 'all'

    const groupByRaw = searchParams.get('group_by') || 'building'
    const group_by = VALID_GROUP_BY.includes(groupByRaw as GroupByOption)
      ? (groupByRaw as GroupByOption)
      : 'building'

    const statusRaw = searchParams.get('status')
    const status = statusRaw as UnitStatus | undefined

    const columns = parseColumns(searchParams.get('cols'))
    const streams = parseStreams(searchParams.get('streams'))

    return { period, types, segment, group_by, status, columns, streams }
  }, [searchParams])

  const setParams = useCallback(
    (updates: Partial<ReportParams>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)

        if (updates.period !== undefined) {
          if (updates.period === defaultPeriod()) next.delete('period')
          else next.set('period', updates.period)
        }

        if (updates.types !== undefined) {
          const sorted = [...updates.types].sort().join(',')
          if (sorted === DEFAULT_TYPES_SORTED) next.delete('types')
          else next.set('types', updates.types.join(','))
        }

        if (updates.segment !== undefined) {
          if (updates.segment === 'all') next.delete('segment')
          else next.set('segment', updates.segment)
        }

        if (updates.group_by !== undefined) {
          if (updates.group_by === 'building') next.delete('group_by')
          else next.set('group_by', updates.group_by)
        }

        if (updates.status !== undefined) {
          next.set('status', updates.status)
        } else if ('status' in updates && updates.status === undefined) {
          next.delete('status')
        }

        if (updates.columns !== undefined) {
          const encoded = encodeColumns(updates.columns)
          if (encoded) next.set('cols', encoded)
          else next.delete('cols')
        }

        if (updates.streams !== undefined) {
          const sorted = [...updates.streams].sort().join(',')
          if (sorted === DEFAULT_STREAMS_SORTED) next.delete('streams')
          else next.set('streams', updates.streams.join(','))
        }

        return next
      }, { replace: true })
    },
    [setSearchParams],
  )

  const clearStatus = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('status')
      return next
    }, { replace: true })
  }, [setSearchParams])

  const setColumns = useCallback(
    (columns: string[]) => {
      // Ensure core columns are always present
      const withCore = new Set(columns)
      for (const c of CORE_COL_IDS) withCore.add(c)
      setParams({ columns: Array.from(withCore) })
    },
    [setParams],
  )

  return { params, setParams, clearStatus, setColumns }
}
